import React, { useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Plus, Trash2, Copy, Save, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

const generateRowId = () => `row_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

export const BulkEntryGrid = ({
  columns,
  sharedDefaults = null,
  onSave,
  entityName = 'Record',
  testIdPrefix = 'bulk',
}) => {
  const makeEmptyRow = useCallback(() => {
    const row = { _rowId: generateRowId(), _error: null };
    columns.forEach(col => {
      row[col.key] = sharedDefaults?.[col.key] ?? col.defaultValue ?? '';
    });
    return row;
  }, [columns, sharedDefaults]);

  const [rows, setRows] = useState(() => [makeEmptyRow(), makeEmptyRow(), makeEmptyRow()]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const addRow = () => setRows(prev => [...prev, makeEmptyRow()]);

  const removeRow = (rowId) => {
    setRows(prev => prev.length <= 1 ? prev : prev.filter(r => r._rowId !== rowId));
  };

  const duplicateRow = (rowId) => {
    setRows(prev => {
      const source = prev.find(r => r._rowId === rowId);
      if (!source) return prev;
      const idx = prev.indexOf(source);
      const copy = { ...source, _rowId: generateRowId(), _error: null };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  };

  const updateCell = (rowId, key, value) => {
    setRows(prev => prev.map(r => {
      if (r._rowId !== rowId) return r;
      const updated = { ...r, [key]: value, _error: null };
      const col = columns.find(c => c.key === key);
      if (col?.onCellChange) {
        const extra = col.onCellChange(value, updated);
        if (extra) Object.assign(updated, extra);
      }
      return updated;
    }));
    setResult(null);
  };

  // Apply shared defaults to all rows when defaults change
  const applyDefaults = (defaults) => {
    setRows(prev => prev.map(r => {
      const updated = { ...r };
      Object.entries(defaults).forEach(([k, v]) => { if (v) updated[k] = v; });
      return updated;
    }));
  };

  const validateRows = () => {
    let hasError = false;
    const validated = rows.map(row => {
      for (const col of columns) {
        if (col.required) {
          const val = (row[col.key] ?? '').toString().trim();
          if (!val) {
            hasError = true;
            return { ...row, _error: `${col.label} is required` };
          }
        }
        if (col.validate) {
          const err = col.validate(row[col.key], row);
          if (err) {
            hasError = true;
            return { ...row, _error: err };
          }
        }
      }
      return { ...row, _error: null };
    });
    setRows(validated);
    return !hasError;
  };

  const handleSave = async () => {
    if (!validateRows()) return;
    setSaving(true);
    setResult(null);
    try {
      const cleanRows = rows.map(r => {
        const clean = {};
        columns.forEach(col => { clean[col.key] = r[col.key]; });
        return clean;
      });
      const res = await onSave(cleanRows);
      setResult(res);
      // Mark errored rows
      if (res.errors?.length) {
        setRows(prev => {
          const next = [...prev];
          res.errors.forEach(e => {
            if (next[e.row]) next[e.row] = { ...next[e.row], _error: e.detail };
          });
          return next;
        });
      }
      // Remove successfully created rows
      if (res.success_count > 0 && res.errors?.length) {
        const errorIdxs = new Set(res.errors.map(e => e.row));
        setRows(prev => prev.filter((_, i) => errorIdxs.has(i)));
      } else if (res.success_count > 0 && !res.errors?.length) {
        setRows([makeEmptyRow(), makeEmptyRow(), makeEmptyRow()]);
      }
    } catch (err) {
      setResult({ success_count: 0, error_count: rows.length, errors: [{ row: 0, detail: err.response?.data?.detail || err.message }] });
    } finally {
      setSaving(false);
    }
  };

  const validCount = rows.filter(r => !r._error && columns.every(c => !c.required || (r[c.key]?.toString().trim()))).length;
  const errorCount = rows.filter(r => r._error).length;

  return (
    <div data-testid={`${testIdPrefix}-grid`} className="space-y-4">
      {/* Shared Defaults Bar */}
      {sharedDefaults !== null && (
        <div data-testid={`${testIdPrefix}-shared-defaults`} className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--c-50)', borderColor: 'var(--c-200)' }}>
          <p className="text-xs font-medium mb-3 theme-text-dark">Shared Defaults — applied to all rows</p>
          <div className="flex flex-wrap gap-3">
            {columns.filter(c => c.sharedDefault).map(col => (
              <div key={col.key} className="w-48">
                <label className="text-[11px] text-slate-500 mb-1 block">{col.label}</label>
                {col.type === 'select' ? (
                  <Select value={sharedDefaults[col.key] || ''} onValueChange={v => {
                    const updated = { ...sharedDefaults, [col.key]: v };
                    applyDefaults({ [col.key]: v });
                    col.onSharedChange?.(updated);
                  }}>
                    <SelectTrigger className="h-8 text-sm bg-white"><SelectValue placeholder={`Select ${col.label}`} /></SelectTrigger>
                    <SelectContent>
                      {(col.options || []).map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input className="h-8 text-sm" value={sharedDefaults[col.key] || ''} onChange={e => {
                    applyDefaults({ [col.key]: e.target.value });
                    col.onSharedChange?.({ ...sharedDefaults, [col.key]: e.target.value });
                  }} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Grid Table */}
      <div className="border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-500 w-10">#</th>
              {columns.map(col => (
                <th key={col.key} className="px-3 py-2.5 text-left text-xs font-medium text-slate-500" style={col.width ? { width: col.width } : {}}>
                  {col.label}{col.required && <span className="text-red-400 ml-0.5">*</span>}
                </th>
              ))}
              <th className="px-3 py-2.5 text-center text-xs font-medium text-slate-500 w-20">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row._rowId} data-testid={`${testIdPrefix}-row-${idx}`}
                className={`border-b border-slate-100 transition-colors ${row._error ? 'bg-red-50/50' : 'hover:bg-slate-50/50'}`}
              >
                <td className="px-3 py-2 text-slate-400 text-xs">{idx + 1}</td>
                {columns.map(col => {
                  const opts = col.getOptions ? col.getOptions(row) : (col.options || []);
                  return (
                  <td key={col.key} className="px-2 py-1.5">
                    {col.readOnly ? (
                      <span data-testid={`${testIdPrefix}-${idx}-${col.key}`}
                        className="block h-8 px-3 py-1.5 text-sm text-slate-600 bg-slate-50 rounded-md border border-slate-200 truncate">
                        {row[col.key] || '-'}
                      </span>
                    ) : col.type === 'select' ? (
                      <Select value={row[col.key] || ''} onValueChange={v => updateCell(row._rowId, col.key, v)}>
                        <SelectTrigger data-testid={`${testIdPrefix}-${idx}-${col.key}`}
                          className={`h-8 text-sm ${row._error && col.required && !row[col.key] ? 'border-red-300' : ''}`}>
                          <SelectValue placeholder={col.placeholder || 'Select...'} />
                        </SelectTrigger>
                        <SelectContent>
                          {opts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input data-testid={`${testIdPrefix}-${idx}-${col.key}`}
                        type={col.type || 'text'}
                        className={`h-8 text-sm ${row._error && col.required && !row[col.key]?.toString().trim() ? 'border-red-300' : ''}`}
                        placeholder={col.placeholder || ''}
                        value={row[col.key] || ''}
                        onChange={e => updateCell(row._rowId, col.key, e.target.value)}
                      />
                    )}
                  </td>
                  );
                })}
                <td className="px-2 py-1.5">
                  <div className="flex items-center justify-center gap-1">
                    <TooltipProvider delayDuration={200}>
                      <Tooltip><TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => duplicateRow(row._rowId)} data-testid={`${testIdPrefix}-dup-${idx}`}>
                          <Copy className="h-3.5 w-3.5 text-slate-400" />
                        </Button>
                      </TooltipTrigger><TooltipContent side="top"><p>Duplicate</p></TooltipContent></Tooltip>
                      <Tooltip><TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeRow(row._rowId)} data-testid={`${testIdPrefix}-del-${idx}`}
                          disabled={rows.length <= 1}>
                          <Trash2 className="h-3.5 w-3.5 text-red-400" />
                        </Button>
                      </TooltipTrigger><TooltipContent side="top"><p>Remove</p></TooltipContent></Tooltip>
                    </TooltipProvider>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Inline errors */}
        {rows.some(r => r._error) && (
          <div className="px-4 py-2 bg-red-50 border-t border-red-100">
            {rows.map((r, idx) => r._error ? (
              <p key={r._rowId} className="text-xs text-red-600 flex items-center gap-1.5 py-0.5">
                <AlertCircle className="h-3 w-3 flex-shrink-0" />Row {idx + 1}: {r._error}
              </p>
            ) : null)}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={addRow} data-testid={`${testIdPrefix}-add-row`} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Add Row
          </Button>
          <div className="text-xs text-slate-500">
            {rows.length} row{rows.length !== 1 ? 's' : ''} · <span className="text-green-600">{validCount} valid</span>
            {errorCount > 0 && <> · <span className="text-red-500">{errorCount} error{errorCount !== 1 ? 's' : ''}</span></>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {result && (
            <Badge variant="outline" className={result.success_count > 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}>
              {result.success_count > 0 ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <AlertCircle className="h-3 w-3 mr-1" />}
              {result.success_count} saved{result.error_count > 0 ? `, ${result.error_count} failed` : ''}
            </Badge>
          )}
          <Button onClick={handleSave} disabled={saving || rows.length === 0} data-testid={`${testIdPrefix}-save-all`}
            className="gap-1.5" style={{ backgroundColor: 'var(--c-600)' }}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save All ({validCount})
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkEntryGrid;
